import moment from 'moment';
import BookingData, { BookingLine, Extra } from '../models/BookingData';
import { AGENT } from './constants';

// Misma regla que se usa para calcular el totalToPay al hacer la reserva:
// si paga el agente con su tarjeta se cobra el neto, en cualquier otro caso
// se cobra lo que paga el cliente (el pvp, o el neto más la comisión).
const getBookingLineAmount = (bookingLine: BookingLine, isNetPrice: boolean, isAgentCardPayment: boolean): number => {
  if (isAgentCardPayment) return bookingLine.net;

  return isNetPrice ? bookingLine.net + bookingLine.commission : bookingLine.pvp;
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatMoney = (amount: number | undefined, currencyCode: string): string => {
  const value = Number(amount ?? 0);

  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currencyCode || 'EUR'
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currencyCode || ''}`.trim();
  }
};

const formatDate = (value: string | Date | undefined): string => {
  if (!value) return '-';
  const date = moment(value);
  return date.isValid() ? date.format('DD/MM/YYYY') : '-';
};

const getStatusColor = (statusName: string): string => {
  const normalized = statusName.toLowerCase();

  if (normalized.includes('cancel') || normalized.includes('error')) return '#dc2626';
  if (normalized.includes('pending') || normalized.includes('postponed')) return '#d97706';
  if (normalized.includes('active') || normalized.includes('confirmed')) return '#059669';
  return '#4b5563';
};

const getOccupancyText = (bookingLine: BookingLine): string => {
  const adults = bookingLine.paxes.filter((pax) => pax.paxType === 0 || pax.paxType === 1).length;
  const children = bookingLine.paxes.filter((pax) => pax.paxType === 2 || pax.paxType === 3).length;

  return `${adults} adulto${adults === 1 ? '' : 's'}${children > 0 ? ` · ${children} niño${children === 1 ? '' : 's'}` : ''}`;
};

const renderKeyValue = (label: string, value: string): string => `
  <tr>
    <td style="padding:10px 0;color:#6b7280;font-size:13px;width:38%;vertical-align:top;">${escapeHtml(label)}</td>
    <td style="padding:10px 0;color:#111827;font-size:13px;font-weight:600;vertical-align:top;text-align:right;">${escapeHtml(value)}</td>
  </tr>
`;

const renderRoom = (bookingLine: BookingLine, currencyCode: string, index: number, amount: number): string => `
  <tr>
    <td style="padding:16px 0;border-top:1px solid #e5e7eb;">
      <div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:4px;">${index + 1}. ${escapeHtml(bookingLine.name)}</div>
      <div style="font-size:12px;color:#6b7280;margin-bottom:6px;">${escapeHtml(bookingLine.regimeName)}</div>
      <div style="font-size:12px;color:#374151;margin-bottom:4px;">Entrada: ${formatDate(bookingLine.checkIn)} · Salida: ${formatDate(bookingLine.checkOut)}</div>
      <div style="font-size:12px;color:#374151;">${escapeHtml(getOccupancyText(bookingLine))}</div>
    </td>
    <td style="padding:16px 0;border-top:1px solid #e5e7eb;text-align:right;white-space:nowrap;font-size:14px;font-weight:700;color:#111827;">
      ${escapeHtml(formatMoney(amount, currencyCode))}
    </td>
  </tr>
`;

const renderExtra = (extra: Extra, currencyCode: string, index: number): string => `
  <tr>
    <td style="padding:16px 0;border-top:1px solid #e5e7eb;">
      <div style="font-size:14px;font-weight:700;color:#111827;margin-bottom:4px;">${index + 1}. ${escapeHtml(extra.description)}</div>
      <div style="font-size:12px;color:#374151;">Cantidad: ${extra.quantity} · ${formatDate(extra.applicationDateFrom)} - ${formatDate(extra.applicationDateTo)}</div>
    </td>
    <td style="padding:16px 0;border-top:1px solid #e5e7eb;text-align:right;white-space:nowrap;font-size:14px;font-weight:700;color:#111827;">
      ${escapeHtml(formatMoney(extra.price, currencyCode))}
    </td>
  </tr>
`;

export const buildBookingDetailsEmailHtml = (bookingData: BookingData): string => {
  const statusColor = getStatusColor(bookingData.statusName || '');
  const isAgentCardPayment = (bookingData.paymentMethodCode || '').toLowerCase().includes('tpv') && bookingData.typeCardPayment === AGENT;
  const linesTotal = bookingData.bookingLines.reduce((acc, line) => acc + getBookingLineAmount(line, bookingData.isNetPrice, isAgentCardPayment), 0);
  const total = bookingData.totalToPay ?? linesTotal;
  const paid = bookingData.payed ?? 0;
  const pending = Math.max(total - paid, 0);
  const fullName = [bookingData.ownerData?.name, bookingData.ownerData?.surname, bookingData.ownerData?.secondSurname]
    .filter(Boolean)
    .join(' ');

  return `
    <!doctype html>
    <html>
      <body style="font-family: Arial, sans-serif; background-color: #f7f7fb; padding: 20px; margin: 0;">
        <div style="max-width: 600px; margin: auto; background-color: #ffffff; border-radius: 10px; padding: 40px 30px; text-align: center; box-shadow: 0 0 10px rgba(0,0,0,0.05);">
          <div style="margin-bottom: 20px;">
            <img src="[url_chain_logo]" width="160" height="auto" style="display:block; width:160px; height:auto; margin:0 auto;" alt="[client_name]" />
          </div>

          <h2 style="color: #333333; margin-bottom: 20px;">Detalles de la reserva</h2>

          <div style="text-align: center; font-size: 14px; color: #333333; line-height: 1.6; margin-bottom: 20px;">
            <p style="margin: 0 0 8px 0;">
              Reserva <strong>${escapeHtml(bookingData.pmsLocator || '-')}</strong> en
              <strong>${escapeHtml(bookingData.hotelInfo?.name || '[client_name]')}</strong>.
            </p>
            <p style="margin: 0;">
              Estado:
              <strong style="color:${statusColor};">${escapeHtml(bookingData.statusName || '-')}</strong>
            </p>
          </div>

          <div style="font-size: 16px; font-weight: bold; color: #333333; background-color: #f4f4f4; padding: 12px 20px; border-radius: 6px; display: inline-block; margin-bottom: 20px;">
            Total: ${escapeHtml(formatMoney(total, bookingData.currencyCode))}
          </div>

          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-top: 10px; border-collapse: collapse;">
            ${renderKeyValue('Localizador Pro', bookingData.proLocator || '-')}
            ${renderKeyValue('Titular', fullName || '-')}
            ${renderKeyValue('Email', bookingData.ownerData?.email || '-')}
            ${renderKeyValue('Teléfono', bookingData.ownerData?.phone || '-')}
          </table>

          <hr style="border: none; border-top: 1px solid #eeeeee; margin: 20px 0;">

          <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-top: 10px; border-collapse: collapse;">
            ${renderKeyValue('Entrada', formatDate(bookingData.bookingLines[0]?.checkIn))}
            ${renderKeyValue('Salida', formatDate(bookingData.bookingLines[0]?.checkOut))}
            ${renderKeyValue('Método de pago', bookingData.paymentMethodName || bookingData.paymentMethodCode || '-')}
            ${renderKeyValue('Pagado', formatMoney(paid, bookingData.currencyCode))}
            ${renderKeyValue('Pendiente', formatMoney(pending, bookingData.currencyCode))}
          </table>

          <div style="text-align:left; margin-top:22px;">
            <div style="font-size:14px; font-weight:700; color:#333333; margin-bottom:6px;">Habitaciones</div>
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">
              ${bookingData.bookingLines.map((line, index) => renderRoom(line, bookingData.currencyCode, index, getBookingLineAmount(line, bookingData.isNetPrice, isAgentCardPayment))).join('')}
            </table>
          </div>

          ${bookingData.extras?.length ? `
            <div style="text-align:left; margin-top:22px;">
              <div style="font-size:14px; font-weight:700; color:#333333; margin-bottom:6px;">Servicios extra</div>
              <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="border-collapse: collapse;">
                ${bookingData.extras.map((extra, index) => renderExtra(extra, bookingData.currencyCode, index)).join('')}
              </table>
            </div>
          ` : ''}

          <hr style="border: none; border-top: 1px solid #eeeeee; margin: 30px 0;">

          <div style="font-size: 12px; color: #999999; text-align: center;">
            <div style="margin-bottom: 10px;">
              <img src="[url_chain_logo]" width="160" height="auto" style="display:block; width:160px; height:auto; margin:0 auto;" alt="[client_name]" />
            </div>
            <p style="margin: 5px 0;">
              <a href="http://[tenant_url]/[language]/privacity" style="color: #999999; text-decoration: none;">Privacy Policy</a> |
              <a href="http://[tenant_url]/[language]/contact" style="color: #999999; text-decoration: none;">Contact</a>
            </p>
            <p style="margin: 5px 0;">© [year] [client_name]. All rights reserved.</p>
          </div>
        </div>
      </body>
    </html>
  `;
};
